import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowCounterClockwise,
  ArrowsClockwise,
  BellRinging,
  CaretRight,
  CheckCircle,
  Clock,
  ClockCounterClockwise,
  Envelope,
  Eye,
  ListBullets,
  MagnifyingGlass,
  Robot,
  User,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import { revertCommand, type CommandIntent } from '@/api/commands';
import { type AuditEntry } from '@/api/audit';
import {
  listActivityFeed,
  listActivityFilters,
  type ListFeedOpts,
} from '@/api/activity';
import { listEmailBatches, listEmailRecipients, type EmailBatch } from '@/api/emaillog';
import {
  listPushBatches,
  listPushRecipients,
  type PushBatch,
  type PushStatus,
} from '@/api/pushlog';
import { ApiError } from '@/api/client';
import { refundInvoice } from '@/api/billing';
import { cn } from '@/lib/utils';
import { formatActivitySummary } from '@/lib/activityMeta';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/LoadError';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  TablePagination,
  useTablePaginationState,
} from '@/components/ui/table-pagination';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  ActivityFilterPanel,
  EMPTY_ACTIVITY_FILTERS,
  NOTICE_ACTION_GROUP,
  countActiveActivityFilters,
  type ActivityFilters,
  type ActorOption,
} from '@/components/ActivityFilterPanel';
import {
  EmailLogFilterPanel,
  EMPTY_EMAIL_LOG_FILTERS,
  countActiveEmailLogFilters,
  type EmailLogFilters,
} from '@/components/EmailLogFilterPanel';
import { AuditChangesTable } from '@/components/AuditChangesTable';
import { EmailPreviewDialog } from '@/components/EmailPreviewDialog';
import { PushPreviewDialog } from '@/components/PushPreviewDialog';

const META_SEPARATOR = ' · ';

// emailStatusBadgeClass tints the per-recipient delivery-status pill: a
// successful send gets the lime (positive) fill, a hard SMTP failure is red,
// and a policy block (e.g. domain not allowed) is neutral.
function emailStatusBadgeClass(status: string): string {
  switch (status) {
    case 'sent':
      return 'bg-primary text-primary-foreground';
    case 'failed':
      return 'bg-destructive/10 text-destructive';
    default: // 'blocked' and any future status
      return 'bg-muted text-muted-foreground';
  }
}

// teamNamesFromMeta pulls the denormalized team list captured at audit-write
// time for activity-scoped actions (calling_*, activity_*, attendance_confirm).
// Empty when the action isn't team-scoped — the badge cell stays blank rather
// than guessing.
function teamNamesFromMeta(meta: Record<string, unknown>): string[] {
  const v = meta.team_names;
  if (!Array.isArray(v)) return [];
  return v.filter((n): n is string => typeof n === 'string' && n.length > 0);
}

// emailReasonBadgeClass tints the reason pill to echo its outcome class:
// 'ok' is positive, smtp_* are delivery failures (red), and the pre-send
// blocks (domain_not_allowed, invalid_address, invalid_subject) are neutral.
function emailReasonBadgeClass(reason: string): string {
  // 'ok' gets a green outline + soft tint: a faint lime fill alone is nearly
  // invisible on white, so the border is what makes it read as a badge while
  // staying lighter than the bold lime status pill next to it.
  if (reason === 'ok') return 'border border-lime bg-lime/15 text-foreground';
  if (reason.startsWith('smtp_')) return 'bg-destructive/10 text-destructive';
  return 'bg-muted text-muted-foreground';
}

export function Activity() {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const tabParam = searchParams.get('tab');
  const activeTab: 'actions' | 'email' | 'push' =
    tabParam === 'email' ? 'email' : tabParam === 'push' ? 'push' : 'actions';
  const setTab = useCallback(
    (tab: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        next.delete('page');
        return next;
      });
    },
    [setSearchParams],
  );

  const dateFmt = new Intl.DateTimeFormat(i18n.language || undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: false,
  });

  const [actionsFilterOpen, setActionsFilterOpen] = useState(false);
  const [actionsFilters, setActionsFilters] = useState<ActivityFilters>(EMPTY_ACTIVITY_FILTERS);

  // ?preset=notices (deep-link from Notices → Members "notice activity log")
  // pre-selects the notice/fee action group once, then consumes the param so a
  // later manual filter clear isn't re-applied on the next render.
  useEffect(() => {
    if (searchParams.get('preset') !== 'notices') return;
    setActionsFilters((prev) => ({ ...prev, actions: NOTICE_ACTION_GROUP }));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('preset');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);
  const [emailFilterOpen, setEmailFilterOpen] = useState(false);
  const [emailFilters, setEmailFilters] = useState<EmailLogFilters>(EMPTY_EMAIL_LOG_FILTERS);

  const activeFilterCount =
    activeTab === 'actions'
      ? countActiveActivityFilters(actionsFilters)
      : activeTab === 'email'
        ? countActiveEmailLogFilters(emailFilters)
        : 0;
  const setFilterOpen =
    activeTab === 'actions' ? setActionsFilterOpen : activeTab === 'email' ? setEmailFilterOpen : () => {};
  const filterButtonHidden = activeTab === 'push';

  // Spin the refresh icon while any of the active tab's queries are in flight.
  const refreshingCount = useIsFetching({
    predicate: (q) => {
      const key0 = q.queryKey[0];
      if (typeof key0 !== 'string') return false;
      if (activeTab === 'actions') {
        return key0 === 'activity-feed' || key0 === 'activity-filters';
      }
      if (activeTab === 'email') {
        return key0 === 'email-log' || key0 === 'email-recipients';
      }
      return key0 === 'push-log' || key0 === 'push-recipients';
    },
  });
  const isRefreshing = refreshingCount > 0;

  const refresh = useCallback(() => {
    if (activeTab === 'actions') {
      void queryClient.invalidateQueries({ queryKey: ['activity-feed', slug] });
      void queryClient.invalidateQueries({ queryKey: ['activity-filters', slug] });
    } else if (activeTab === 'email') {
      void queryClient.invalidateQueries({ queryKey: ['email-log', slug] });
      void queryClient.invalidateQueries({ queryKey: ['email-recipients', slug] });
    } else {
      void queryClient.invalidateQueries({ queryKey: ['push-log', slug] });
      void queryClient.invalidateQueries({ queryKey: ['push-recipients', slug] });
    }
  }, [activeTab, queryClient, slug]);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-8">
      <Tabs value={activeTab} onValueChange={setTab}>
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('settings.title')}
            </p>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
              {t('settings.log.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('settings.activity.subtitle')}</p>
          </div>
          <div className="flex flex-col items-start gap-3 sm:items-end">
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={refresh}
                disabled={isRefreshing}
                aria-label={t('settings.log.refresh')}
                className="flex shrink-0 items-center gap-2 rounded-pill border border-border px-4 py-2 font-heading text-sm font-semibold text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:border-foreground/30 hover:text-foreground disabled:cursor-wait disabled:opacity-60"
              >
                <ArrowsClockwise
                  className={cn('h-4 w-4', isRefreshing && 'animate-spin')}
                  weight="bold"
                  aria-hidden
                />
                {t('settings.log.refresh')}
              </button>
              {!filterButtonHidden && (
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  className={cn(
                    'flex shrink-0 items-center gap-2 rounded-pill border px-4 py-2 font-heading text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    activeFilterCount > 0
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
                  )}
                >
                  {t('settings.activity.filter.button')}
                  {activeFilterCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 font-heading text-[0.65rem] font-bold text-primary-foreground">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              )}
            </div>
            <TabsList>
              <TabsTrigger value="actions">{t('settings.log.tabs.actions')}</TabsTrigger>
              <TabsTrigger value="email">{t('settings.log.tabs.email')}</TabsTrigger>
              <TabsTrigger value="push">{t('settings.log.tabs.push')}</TabsTrigger>
            </TabsList>
          </div>
        </header>

        <TabsContent value="actions" className="mt-4">
          <ActionsTab
            slug={slug}
            token={token}
            dateFmt={dateFmt}
            filterOpen={actionsFilterOpen}
            setFilterOpen={setActionsFilterOpen}
            filters={actionsFilters}
            setFilters={setActionsFilters}
          />
        </TabsContent>

        <TabsContent value="email" className="mt-4">
          <EmailTab
            slug={slug}
            token={token}
            dateFmt={dateFmt}
            filterOpen={emailFilterOpen}
            setFilterOpen={setEmailFilterOpen}
            filters={emailFilters}
            setFilters={setEmailFilters}
          />
        </TabsContent>

        <TabsContent value="push" className="mt-4">
          <PushTab slug={slug} token={token} dateFmt={dateFmt} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Actions tab
// ---------------------------------------------------------------------------

function ActionsTab({
  slug,
  token,
  dateFmt,
  filterOpen,
  setFilterOpen,
  filters,
  setFilters,
}: {
  slug: string;
  token: string | null;
  dateFmt: Intl.DateTimeFormat;
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  filters: ActivityFilters;
  setFilters: (f: ActivityFilters) => void;
}) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const { page, size, setPage, setSize, offset } = useTablePaginationState({
    defaultSize: 25,
  });

  // Every filter is a server parameter, so it belongs in the query key: a
  // filter change fetches a different result set rather than re-slicing one
  // the browser is holding. The datetime-local values are local wall time —
  // toISOString converts them to the instant the server compares against.
  const feedParams = useMemo<ListFeedOpts>(() => {
    const p: ListFeedOpts = { limit: size, offset };
    if (filters.actorUserIds.length > 0) p.actorUserIds = filters.actorUserIds;
    if (filters.actions.length > 0) p.actions = filters.actions;
    if (filters.source) p.source = filters.source;
    if (filters.createdAfter) p.createdAfter = new Date(filters.createdAfter).toISOString();
    if (filters.createdBefore) p.createdBefore = new Date(filters.createdBefore).toISOString();
    return p;
  }, [filters, size, offset]);

  const feedQuery = useQuery({
    queryKey: ['activity-feed', slug, feedParams],
    queryFn: () => listActivityFeed(token, slug, feedParams),
    enabled: !!token && !!slug,
    placeholderData: (prev) => prev,
  });

  // Option sets come from the whole history, not from the rows on screen —
  // otherwise you can only filter by what you can already see.
  const filtersQuery = useQuery({
    queryKey: ['activity-filters', slug],
    queryFn: () => listActivityFilters(token, slug),
    enabled: !!token && !!slug,
    staleTime: 5 * 60 * 1000,
  });

  const actorOptions = useMemo<ActorOption[]>(
    () => (filtersQuery.data?.actors ?? []).map((a) => ({ id: a.id, name: a.name })),
    [filtersQuery.data],
  );
  const actionOptions = useMemo<string[]>(
    () => filtersQuery.data?.actions ?? [],
    [filtersQuery.data],
  );

  const items = feedQuery.data?.items ?? [];
  const total = feedQuery.data?.total ?? 0;
  const isPending = feedQuery.isPending;
  const isError = feedQuery.isError;

  const revertMutation = useMutation({
    mutationFn: async (id: string) => revertCommand(token, slug, id),
    onSuccess: () => {
      setPage(1);
      void queryClient.invalidateQueries();
    },
  });

  return (
    <>
      <ActivityFilterPanel
        open={filterOpen}
        onOpenChange={setFilterOpen}
        filters={filters}
        onChange={(f) => { setFilters(f); setPage(1); }}
        actorOptions={actorOptions}
        actionOptions={actionOptions}
      />

      {isPending && items.length === 0 && <TableSkeleton columns={6} />}
      {isError && <LoadError onRetry={() => { void feedQuery.refetch(); }} />}
      {!isPending && !isError && total === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-background p-10 text-center">
          <ClockCounterClockwise className="h-10 w-10 text-muted-foreground" weight="duotone" />
          <h2 className="font-heading text-lg font-bold">{t('settings.activity.empty.title')}</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {t('settings.activity.empty.body')}
          </p>
        </div>
      )}
      {items.length > 0 && (
        <>
          <div className="rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/40">
                  <TableHead className="w-12" />
                  <TableHead>{t('settings.activity.table.change')}</TableHead>
                  <TableHead className="hidden whitespace-nowrap sm:table-cell">{t('settings.activity.table.team')}</TableHead>
                  <TableHead className="hidden w-40 whitespace-nowrap sm:table-cell">{t('settings.activity.table.when')}</TableHead>
                  <TableHead className="hidden whitespace-nowrap sm:table-cell">{t('settings.activity.table.by')}</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  if (item.command) {
                    const intent = item.command;
                    return (
                      <CommandTableRow
                        key={`cmd-${intent.id}`}
                        intent={intent}
                        dateFmt={dateFmt}
                        changes={item.changes ?? null}
                        onRevert={() => revertMutation.mutate(intent.id)}
                        reverting={revertMutation.isPending && revertMutation.variables === intent.id}
                        revertError={
                          revertMutation.isError && revertMutation.variables === intent.id
                            ? revertErrorMessage(revertMutation.error, t)
                            : null
                        }
                      />
                    );
                  }
                  if (item.audit) {
                    const entry = item.audit;
                    return (
                      <AuditTableRow
                        key={`aud-${entry.id}`}
                        entry={entry}
                        dateFmt={dateFmt}
                        slug={slug}
                        token={token}
                        onAfterRefund={() => {
                          void queryClient.invalidateQueries({ queryKey: ['activity-feed', slug] });
                          void queryClient.invalidateQueries({ queryKey: ['billing-invoices', slug] });
                        }}
                      />
                    );
                  }
                  return null;
                })}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4">
            <TablePagination
              total={total}
              page={page}
              size={size}
              onPageChange={setPage}
              onSizeChange={setSize}
            />
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Email tab
// ---------------------------------------------------------------------------

function EmailTab({
  slug,
  token,
  dateFmt,
  filterOpen,
  setFilterOpen,
  filters,
  setFilters,
}: {
  slug: string;
  token: string | null;
  dateFmt: Intl.DateTimeFormat;
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  filters: EmailLogFilters;
  setFilters: (f: EmailLogFilters) => void;
}) {
  const { t } = useTranslation('common');

  const [debouncedAddressQ, setDebouncedAddressQ] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);

  const { page, size, setPage, setSize, offset } = useTablePaginationState({
    defaultSize: 25,
  });

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedAddressQ(filters.addressQ), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters.addressQ]);

  const batchesQuery = useQuery({
    queryKey: ['email-log', slug, filters.createdAfter, filters.createdBefore, debouncedAddressQ, filters.kind, size, offset],
    queryFn: () => {
      const opts: Parameters<typeof listEmailBatches>[2] = { limit: size, offset };
      if (filters.createdAfter) opts.from = filters.createdAfter;
      if (filters.createdBefore) opts.to = filters.createdBefore;
      if (debouncedAddressQ) opts.q = debouncedAddressQ;
      if (filters.kind) opts.kind = filters.kind;
      return listEmailBatches(token, slug, opts);
    },
    enabled: !!token && !!slug,
    placeholderData: (prev) => prev,
  });

  const total = batchesQuery.data?.total ?? 0;
  const batches = batchesQuery.data?.items ?? [];

  return (
    <>
      <EmailLogFilterPanel
        open={filterOpen}
        onOpenChange={setFilterOpen}
        filters={filters}
        onChange={(f) => { setFilters(f); setPage(1); }}
      />

      {batchesQuery.isPending && batches.length === 0 && (
        <TableSkeleton columns={5} />
      )}
      {batchesQuery.isError && (
        <LoadError onRetry={() => void batchesQuery.refetch()} />
      )}
      {!batchesQuery.isPending && !batchesQuery.isError && total === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-background p-10 text-center">
          <Envelope className="h-10 w-10 text-muted-foreground" weight="duotone" />
          <h2 className="font-heading text-lg font-bold">{t('settings.log.email.empty.title')}</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {t('settings.log.email.empty.body')}
          </p>
        </div>
      )}

      {batches.length > 0 && (
        <>
          <div className="rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/40">
                  <TableHead className="w-12" />
                  <TableHead>{t('settings.log.email.table.subject')}</TableHead>
                  <TableHead className="hidden w-40 whitespace-nowrap sm:table-cell">{t('settings.log.email.table.date')}</TableHead>
                  <TableHead className="hidden w-24 text-right sm:table-cell">{t('settings.log.email.table.recipients')}</TableHead>
                  <TableHead className="hidden w-40 sm:table-cell">{t('settings.log.email.table.deliveries')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch) => (
                  <EmailBatchRows
                    key={batch.batch_id}
                    batch={batch}
                    expanded={expandedBatchId === batch.batch_id}
                    onToggle={() =>
                      setExpandedBatchId((prev) =>
                        prev === batch.batch_id ? null : batch.batch_id,
                      )
                    }
                    slug={slug}
                    token={token}
                    dateFmt={dateFmt}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4">
            <TablePagination
              total={total}
              page={page}
              size={size}
              onPageChange={setPage}
              onSizeChange={setSize}
            />
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Email batch rows (main row + optional expanded recipients)
// ---------------------------------------------------------------------------

function EmailBatchRows({
  batch,
  expanded,
  onToggle,
  slug,
  token,
  dateFmt,
}: {
  batch: EmailBatch;
  expanded: boolean;
  onToggle: () => void;
  slug: string;
  token: string | null;
  dateFmt: Intl.DateTimeFormat;
}) {
  const { t } = useTranslation('common');

  // Per-batch recipient search. Hundreds of recipients in one blast are
  // unscrollable, so this ILIKE-filters to_address server-side (debounced,
  // mirroring the batch-level address search above).
  const [recipientQ, setRecipientQ] = useState('');
  const [debouncedRecipientQ, setDebouncedRecipientQ] = useState('');
  const recipientDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (recipientDebounceRef.current) clearTimeout(recipientDebounceRef.current);
    recipientDebounceRef.current = setTimeout(() => setDebouncedRecipientQ(recipientQ.trim()), 300);
    return () => {
      if (recipientDebounceRef.current) clearTimeout(recipientDebounceRef.current);
    };
  }, [recipientQ]);

  const recipientsQuery = useQuery({
    queryKey: ['email-recipients', slug, batch.batch_id, debouncedRecipientQ],
    queryFn: () => listEmailRecipients(token, slug, batch.batch_id, debouncedRecipientQ || undefined),
    enabled: expanded && !!token,
    placeholderData: (prev) => prev,
  });

  // The "View" button on each recipient opens the preview dialog with
  // that row's id; null means closed. Lifted to the batch row so the
  // dialog instance is reused across recipients in this batch and the
  // user can step through different recipients without remount churn.
  const [previewID, setPreviewID] = useState<string | null>(null);

  const sentAt = new Date(batch.sent_at);

  return (
    <>
      <TableRow
        className="cursor-pointer"
        onClick={onToggle}
        data-state={expanded ? 'selected' : undefined}
      >
        {/* Expand chevron */}
        <TableCell className="w-12 pr-0">
          <span className="flex h-8 w-8 items-center justify-center rounded-pill text-muted-foreground">
            <CaretRight
              className={cn('h-4 w-4 transition-transform duration-150', expanded && 'rotate-90')}
            />
          </span>
        </TableCell>

        {/* Subject + kind badge */}
        <TableCell className="font-heading text-sm font-semibold">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate">{batch.subject}</span>
            {batch.kind && batch.kind !== 'other' && (
              <span className="inline-flex shrink-0 items-center rounded-pill bg-muted px-2 py-0.5 font-heading text-[0.65rem] font-medium text-muted-foreground">
                {t(`settings.log.email.kind.${batch.kind}`, batch.kind)}
              </span>
            )}
          </div>
          {/* Mobile-only metadata */}
          <p className="mt-0.5 text-xs font-normal text-muted-foreground sm:hidden">
            {dateFmt.format(sentAt)}
            {META_SEPARATOR}
            {t('settings.log.email.batch.recipients', { count: batch.total })}
          </p>
        </TableCell>

        {/* Date */}
        <TableCell className="hidden w-40 whitespace-nowrap text-sm text-muted-foreground sm:table-cell">
          {dateFmt.format(sentAt)}
        </TableCell>

        {/* Recipient count */}
        <TableCell className="hidden w-24 text-right text-sm tabular-nums text-muted-foreground sm:table-cell">
          {batch.total}
        </TableCell>

        {/* Delivery status counts */}
        <TableCell className="hidden w-40 sm:table-cell">
          <div className="flex flex-wrap items-center gap-2 text-xs tabular-nums">
            {batch.sent_count > 0 && (
              <span className="text-foreground/70">
                {t('settings.log.email.batch.sent', { count: batch.sent_count })}
              </span>
            )}
            {batch.failed_count > 0 && (
              <span className="text-destructive">
                {t('settings.log.email.batch.failed', { count: batch.failed_count })}
              </span>
            )}
            {batch.blocked_count > 0 && (
              <span className="text-muted-foreground">
                {t('settings.log.email.batch.blocked', { count: batch.blocked_count })}
              </span>
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Expanded recipient sub-table */}
      {expanded && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={5} className="p-0">
            {/* Recipient search — only worth showing once a batch is large. */}
            {batch.total > 10 && (
              <div className="border-b border-border px-6 py-2.5">
                <div className="relative max-w-xs">
                  <MagnifyingGlass
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    inputMode="email"
                    value={recipientQ}
                    onChange={(e) => setRecipientQ(e.target.value)}
                    placeholder={t('settings.log.email.recipients.searchPlaceholder')}
                    aria-label={t('settings.log.email.recipients.searchPlaceholder')}
                    className="h-9 pl-9 text-xs"
                  />
                </div>
              </div>
            )}
            {recipientsQuery.isPending && (
              <p className="px-6 py-3 text-xs text-muted-foreground">{t('common.loading')}</p>
            )}
            {recipientsQuery.isError && (
              <p className="px-6 py-3 text-xs text-foreground">{t('settings.log.email.loadError')}</p>
            )}
            {recipientsQuery.data && recipientsQuery.data.items.length === 0 && (
              <p className="px-6 py-3 text-xs text-muted-foreground">
                {debouncedRecipientQ
                  ? t('settings.log.email.recipients.noMatches', { q: debouncedRecipientQ })
                  : t('settings.log.email.recipients.empty')}
              </p>
            )}
            {recipientsQuery.data && recipientsQuery.data.items.length > 0 && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-6 py-2 text-left font-heading font-semibold text-muted-foreground">
                      {t('settings.log.email.recipients.address')}
                    </th>
                    <th className="px-3 py-2 text-left font-heading font-semibold text-muted-foreground">
                      {t('settings.log.email.recipients.status')}
                    </th>
                    <th className="hidden px-3 py-2 text-left font-heading font-semibold text-muted-foreground sm:table-cell">
                      {t('settings.log.email.recipients.reason')}
                    </th>
                    <th className="w-24 px-3 py-2" aria-hidden />
                  </tr>
                </thead>
                <tbody>
                  {recipientsQuery.data.items.map((r) => (
                    <Fragment key={r.id}>
                      <tr className={cn('border-b border-border/40', !r.detail && 'last:border-0')}>
                        <td className="px-6 py-2 font-mono">{r.to_address}</td>
                        <td className="px-3 py-2">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-pill px-2 py-0.5 font-heading text-[0.65rem] font-semibold',
                              emailStatusBadgeClass(r.status),
                            )}
                          >
                            {t(`settings.log.email.status.${r.status}`)}
                          </span>
                        </td>
                        <td className="hidden px-3 py-2 sm:table-cell">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-pill px-2 py-0.5 font-heading text-[0.65rem] font-medium',
                              emailReasonBadgeClass(r.reason),
                            )}
                          >
                            {t(`settings.log.email.reason.${r.reason}`, { defaultValue: r.reason })}
                          </span>
                        </td>
                        <td className="w-24 px-3 py-2 text-right">
                          {r.has_preview && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewID(r.id)}
                            >
                              <Eye className="h-3.5 w-3.5" weight="bold" aria-hidden />
                              {t('settings.log.email.recipients.view')}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {r.detail && (
                        <tr className="border-b border-border/40 last:border-0">
                          <td colSpan={4} className="px-6 pb-3 pt-0">
                            {/* Raw delivery error from the SMTP attempt. The
                                reason badge is the human-readable bucket; this
                                is the full underlying message — and the only
                                failure detail visible on mobile, where the
                                reason column is hidden. */}
                            <div className="rounded-md bg-destructive/5 px-3 py-2">
                              <span className="font-heading text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('settings.log.email.recipients.errorDetail')}
                              </span>
                              <p className="mt-0.5 whitespace-pre-wrap break-words font-mono text-[0.7rem] text-foreground/80">
                                {r.detail}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </TableCell>
        </TableRow>
      )}

      <EmailPreviewDialog
        open={previewID !== null}
        onOpenChange={(open) => { if (!open) setPreviewID(null); }}
        slug={slug}
        token={token}
        messageId={previewID}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Actions tab row components (render TableRow)
// ---------------------------------------------------------------------------

function CommandTableRow({
  intent,
  dateFmt,
  changes,
  onRevert,
  reverting,
  revertError,
}: {
  intent: CommandIntent;
  dateFmt: Intl.DateTimeFormat;
  changes: Array<Record<string, unknown>> | null;
  onRevert: () => void;
  reverting: boolean;
  revertError: string | null;
}) {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);
  const created = new Date(intent.created_at);
  const hasChanges = !!changes && changes.length > 0;
  const actor =
    intent.actor_name?.trim() ||
    intent.actor_email?.trim() ||
    t('settings.activity.actor.user');

  return (
    <>
      <TableRow
        className={cn(hasChanges && 'cursor-pointer')}
        onClick={hasChanges ? () => setExpanded((e) => !e) : undefined}
        data-state={expanded ? 'selected' : undefined}
      >
        <TableCell className="w-12 pr-0">
          <StatusBadge status={intent.status} />
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-2">
            {hasChanges && (
              <CaretRight
                className={cn(
                  'h-4 w-4 shrink-0 text-foreground/70 transition-transform duration-150',
                  expanded && 'rotate-90',
                )}
                aria-hidden
              />
            )}
            <span className="font-heading text-sm font-semibold text-foreground">
              {intent.plan_summary || intent.user_input || t('settings.activity.unnamed')}
            </span>
            <ActorPill type={intent.actor_kind} />
            {hasChanges && changes && (
              <span className="text-xs font-medium text-muted-foreground">
                {t('settings.activity.changes.count', { count: changes.length })}
              </span>
            )}
          </div>
          {revertError && (
            <p className="mt-1 text-xs text-foreground" role="alert">{revertError}</p>
          )}
          {/* Mobile-only metadata */}
          <p className="mt-0.5 text-xs text-muted-foreground sm:hidden">
            {dateFmt.format(created)}
            {META_SEPARATOR}
            {actor}
            {META_SEPARATOR}
            {t(`settings.activity.status.${intent.status}`)}
          </p>
        </TableCell>
        <TableCell className="hidden whitespace-nowrap sm:table-cell" />
        <TableCell className="hidden w-40 whitespace-nowrap text-sm text-muted-foreground sm:table-cell">
          {dateFmt.format(created)}
        </TableCell>
        <TableCell className="hidden whitespace-nowrap text-sm text-muted-foreground sm:table-cell">
          {actor}
        </TableCell>
        <TableCell className="w-24 text-right">
          {intent.revertable && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onRevert(); }}
              disabled={reverting}
              className="shrink-0"
            >
              <ArrowCounterClockwise className="h-4 w-4" />
              {reverting ? t('settings.activity.reverting') : t('settings.activity.revert')}
            </Button>
          )}
        </TableCell>
      </TableRow>
      {expanded && hasChanges && changes && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={6} className="p-0">
            <AuditChangesTable changes={changes} action="command_executed" dateFmt={dateFmt} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function AuditTableRow({
  entry,
  dateFmt,
  slug,
  token,
  onAfterRefund,
}: {
  entry: AuditEntry;
  dateFmt: Intl.DateTimeFormat;
  slug: string;
  token: string | null;
  onAfterRefund: () => void;
}) {
  const { t, i18n } = useTranslation('common');
  const confirm = useConfirm();
  const [expanded, setExpanded] = useState(false);
  const created = new Date(entry.created_at);

  const titleKey = `settings.activity.action.${entry.action}.summary`;
  const meta = (entry.metadata ?? {}) as Record<string, unknown>;
  const customSummary = formatActivitySummary(t, entry.action, meta, i18n.language);
  const translated = t(titleKey, { ...meta, defaultValue: entry.summary });
  const fallback = translated.includes('{{') ? entry.summary : translated;
  // Last-resort guard: an action whose i18n template needs metadata the
  // row doesn't carry (and whose stored summary is empty) would otherwise
  // render a blank cell. Fall back to the generic "(unnamed action)" label.
  const title = (customSummary ?? fallback).trim() || t('settings.activity.unnamed');

  // Generic field-level changes are {field, old, new}; attendance_confirm
  // rows carry richer {member_id, activity_id, member_name, activity_title,
  // activity_starts_at, old, new} objects resolved server-side at read time.
  const changes = Array.isArray(meta.changes)
    ? (meta.changes as Array<Record<string, unknown>>)
    : null;
  const hasChanges = !!changes && changes.length > 0;
  const teamNames = teamNamesFromMeta(meta);
  // calling_create rows carry the count of members the calling reached (the
  // live roster minus exclusions), captured at send time. Per-recipient
  // delivery detail lives in the Push tab; this is the headline number.
  const recipientCount =
    entry.action === 'calling_create' && typeof meta.recipient_count === 'number'
      ? meta.recipient_count
      : null;

  // Whenever a row resolves to a real person, name them — even when
  // actor_type is system/worker/ai. Those rows still carry the user whose
  // action triggered them (e.g. the auto-created member profile behind an
  // admin's first login), so the actor filter matches on that id; showing
  // "System" here made a correctly-filtered row look like a filter leak.
  // The kind stays visible via ActorBadge + ActorPill on the same row.
  const actor =
    entry.actor_name?.trim() ||
    entry.actor_email?.trim() ||
    t(`settings.activity.actor.${entry.actor_type}`);

  const isPayment = entry.action === 'payment_recorded';
  const invoiceID = (meta.invoice_id as string | undefined) ?? '';
  const ageDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
  const showRefund = isPayment && invoiceID !== '' && ageDays < 30;

  const refundMutation = useMutation({
    mutationFn: async () => {
      const ok = await confirm({
        title: t('settings.activity.refund.confirmTitle', { defaultValue: 'Refund this payment?' }),
        body: t('settings.activity.refund.confirmBody', {
          defaultValue: 'This issues a full refund via Stripe. The customer is notified by email.',
        }),
        confirmLabel: t('settings.activity.refund.cta', { defaultValue: 'Refund' }),
        tone: 'destructive',
      });
      if (!ok) throw new Error('cancelled');
      return refundInvoice(token, slug, invoiceID, {});
    },
    onSuccess: () => { onAfterRefund(); },
  });

  return (
    <>
      <TableRow
        className={cn(hasChanges && 'cursor-pointer')}
        onClick={hasChanges ? () => setExpanded((e) => !e) : undefined}
        data-state={expanded ? 'selected' : undefined}
      >
        <TableCell className="w-12 pr-0">
          <ActorBadge type={entry.actor_type} />
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-2">
            {hasChanges && (
              <CaretRight
                className={cn(
                  'h-4 w-4 shrink-0 text-foreground/70 transition-transform duration-150',
                  expanded && 'rotate-90',
                )}
                aria-hidden
              />
            )}
            <span className="font-heading text-sm font-semibold text-foreground">{title}</span>
            <ActorPill type={entry.actor_type} />
            {hasChanges && changes && (
              <span className="text-xs font-medium text-muted-foreground">
                {t('settings.activity.changes.count', { count: changes.length })}
              </span>
            )}
          </div>
          {recipientCount != null && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('settings.activity.calling.recipients', { count: recipientCount })}
            </p>
          )}
          {refundMutation.isError && refundMutation.error instanceof ApiError && (
            <p className="mt-1 text-xs text-red-600">
              {t(`settings.activity.refund.error.${refundMutation.error.code}`, {
                defaultValue: t('settings.activity.refund.error.unknown', { defaultValue: 'Refund failed.' }) ?? '',
              })}
            </p>
          )}
          {/* Mobile-only metadata */}
          <p className="mt-0.5 text-xs text-muted-foreground sm:hidden">
            {dateFmt.format(created)}
            {META_SEPARATOR}
            {actor}
            {teamNames.length > 0 && (
              <>
                {META_SEPARATOR}
                {teamNames.join(', ')}
              </>
            )}
          </p>
        </TableCell>
        <TableCell className="hidden whitespace-nowrap sm:table-cell">
          <TeamBadge names={teamNames} />
        </TableCell>
        <TableCell className="hidden w-40 whitespace-nowrap text-sm text-muted-foreground sm:table-cell">
          {dateFmt.format(created)}
        </TableCell>
        <TableCell className="hidden whitespace-nowrap text-sm text-muted-foreground sm:table-cell">
          {actor}
        </TableCell>
        <TableCell className="w-24 text-right">
          {showRefund && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={refundMutation.isPending}
              onClick={(e) => { e.stopPropagation(); refundMutation.mutate(); }}
            >
              {refundMutation.isPending
                ? t('settings.activity.refund.pending', { defaultValue: 'Refunding…' })
                : t('settings.activity.refund.cta', { defaultValue: 'Refund' })}
            </Button>
          )}
        </TableCell>
      </TableRow>
      {expanded && hasChanges && changes && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={6} className="p-0">
            <AuditChangesTable changes={changes} action={entry.action} dateFmt={dateFmt} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared badge components
// ---------------------------------------------------------------------------

// TeamBadge renders the team(s) an activity-scoped audit row touched. One name
// fits inline as a single pill; a longer list collapses to "first +N" with a
// title attribute carrying the full set so hover/screen-reader users still see
// everything. Empty list → nothing rendered (cell stays blank).
function TeamBadge({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  if (names.length === 1) {
    return (
      <span className="inline-flex max-w-[14rem] items-center truncate rounded-pill border border-border bg-muted/50 px-2.5 py-0.5 font-heading text-xs font-semibold text-foreground/80">
        {names[0]}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span className="inline-flex max-w-[10rem] items-center truncate rounded-pill border border-border bg-muted/50 px-2.5 py-0.5 font-heading text-xs font-semibold text-foreground/80">
        {names[0]}
      </span>
      <span
        className="inline-flex items-center rounded-pill bg-muted px-2 py-0.5 font-heading text-xs font-semibold text-muted-foreground"
        title={names.slice(1).join(', ')}
      >
        +{names.length - 1}
      </span>
    </span>
  );
}

function ActorBadge({ type }: { type: AuditEntry['actor_type'] }) {
  const map: Record<AuditEntry['actor_type'], { Icon: typeof User; className: string }> = {
    user: { Icon: User, className: 'text-foreground/70' },
    ai: { Icon: Robot, className: 'text-foreground/70' },
    worker: { Icon: ListBullets, className: 'text-foreground/50' },
    system: { Icon: ListBullets, className: 'text-foreground/40' },
  };
  const entry = map[type];
  const Icon = entry.Icon;
  return (
    <span
      aria-hidden
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-muted ${entry.className}`}
    >
      <Icon className="h-4 w-4" weight="duotone" />
    </span>
  );
}

// ActorPill renders an inline labelled badge next to the row title for non-
// user actors. Pairs with the icon-only ActorBadge in the leading column so
// the actor's nature is communicated both at-a-glance (icon) and explicitly
// (label). Returns null for user actors to keep the common case uncluttered.
function ActorPill({ type }: { type: AuditEntry['actor_type'] }) {
  const { t } = useTranslation('common');
  if (type === 'user') return null;
  const config: Record<
    Exclude<AuditEntry['actor_type'], 'user'>,
    { Icon: typeof Robot; className: string; tooltipKey: string }
  > = {
    ai: {
      Icon: Robot,
      className: 'border-lime bg-[var(--color-accent-soft)] text-foreground',
      tooltipKey: 'settings.activity.aiBadge.tooltip',
    },
    worker: {
      Icon: ListBullets,
      className: 'border-border bg-muted text-muted-foreground',
      tooltipKey: 'settings.activity.actorPill.worker.tooltip',
    },
    system: {
      Icon: ListBullets,
      className: 'border-border bg-muted text-muted-foreground',
      tooltipKey: 'settings.activity.actorPill.system.tooltip',
    },
  };
  const cfg = config[type];
  const Icon = cfg.Icon;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-pill border px-2 py-0.5 text-[0.6875rem] font-medium leading-none',
        cfg.className,
      )}
      title={t(cfg.tooltipKey)}
    >
      <Icon className="h-3 w-3" weight="duotone" aria-hidden />
      {t(`settings.activity.actor.${type}`)}
    </span>
  );
}

function StatusBadge({ status }: { status: CommandIntent['status'] }) {
  const map: Record<CommandIntent['status'], { Icon: typeof CheckCircle; className: string }> = {
    executed: { Icon: CheckCircle, className: 'text-foreground/70' },
    pending: { Icon: Clock, className: 'text-foreground/50' },
    expired: { Icon: Clock, className: 'text-foreground/40' },
    rejected: { Icon: XCircle, className: 'text-foreground/40' },
    failed: { Icon: Warning, className: 'text-foreground/60' },
    undone: { Icon: ArrowCounterClockwise, className: 'text-foreground/50' },
  };
  const entry = map[status];
  const Icon = entry.Icon;
  return (
    <span
      aria-hidden
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-muted ${entry.className}`}
    >
      <Icon className="h-4 w-4" weight="duotone" />
    </span>
  );
}

function revertErrorMessage(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'expired':
        return t('settings.activity.error.expired');
      case 'invalid_state':
        return t('settings.activity.error.invalid_state');
      case 'not_found':
        return t('settings.activity.error.not_found');
      default:
        return t('settings.activity.error.unknown');
    }
  }
  return t('settings.activity.error.unknown');
}

// ---------------------------------------------------------------------------
// Push tab
// ---------------------------------------------------------------------------

function pushStatusBadgeClass(status: PushStatus): string {
  switch (status) {
    case 'sent':
      return 'bg-primary text-primary-foreground';
    case 'failed':
      return 'bg-destructive/10 text-destructive';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function PushTab({
  slug,
  token,
  dateFmt,
}: {
  slug: string;
  token: string | null;
  dateFmt: Intl.DateTimeFormat;
}) {
  const { t } = useTranslation('common');
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const { page, size, setPage, setSize, offset } = useTablePaginationState({ defaultSize: 25 });

  const batchesQuery = useQuery({
    queryKey: ['push-log', slug, size, offset],
    queryFn: () => listPushBatches(token, slug, { limit: size, offset }),
    enabled: !!token && !!slug,
    placeholderData: (prev) => prev,
  });

  const total = batchesQuery.data?.total ?? 0;
  const batches = batchesQuery.data?.items ?? [];

  return (
    <>
      {batchesQuery.isPending && batches.length === 0 && (
        <TableSkeleton columns={5} />
      )}
      {batchesQuery.isError && (
        <LoadError onRetry={() => void batchesQuery.refetch()} />
      )}
      {!batchesQuery.isPending && !batchesQuery.isError && total === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-background p-10 text-center">
          <BellRinging className="h-10 w-10 text-muted-foreground" weight="duotone" />
          <h2 className="font-heading text-lg font-bold">{t('settings.log.push.empty.title')}</h2>
          <p className="max-w-md text-sm text-muted-foreground">{t('settings.log.push.empty.body')}</p>
        </div>
      )}

      {batches.length > 0 && (
        <>
          <div className="rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/40">
                  <TableHead className="w-12" />
                  <TableHead>{t('settings.log.push.table.notification')}</TableHead>
                  <TableHead className="hidden w-40 whitespace-nowrap sm:table-cell">{t('settings.log.push.table.date')}</TableHead>
                  <TableHead className="hidden w-24 text-right sm:table-cell">{t('settings.log.push.table.recipients')}</TableHead>
                  <TableHead className="hidden w-40 sm:table-cell">{t('settings.log.push.table.deliveries')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch) => (
                  <PushBatchRows
                    key={batch.batch_id}
                    batch={batch}
                    expanded={expandedBatchId === batch.batch_id}
                    onToggle={() =>
                      setExpandedBatchId((prev) => (prev === batch.batch_id ? null : batch.batch_id))
                    }
                    slug={slug}
                    token={token}
                    dateFmt={dateFmt}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4">
            <TablePagination
              total={total}
              page={page}
              size={size}
              onPageChange={setPage}
              onSizeChange={setSize}
            />
          </div>
        </>
      )}
    </>
  );
}

function PushBatchRows({
  batch,
  expanded,
  onToggle,
  slug,
  token,
  dateFmt,
}: {
  batch: PushBatch;
  expanded: boolean;
  onToggle: () => void;
  slug: string;
  token: string | null;
  dateFmt: Intl.DateTimeFormat;
}) {
  const { t } = useTranslation('common');
  const [recipientQ, setRecipientQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(recipientQ.trim()), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [recipientQ]);

  const recipientsQuery = useQuery({
    queryKey: ['push-recipients', slug, batch.batch_id, debouncedQ],
    queryFn: () => listPushRecipients(token, slug, batch.batch_id, debouncedQ || undefined),
    enabled: expanded && !!token,
    placeholderData: (prev) => prev,
  });

  const [previewID, setPreviewID] = useState<string | null>(null);
  const sentAt = new Date(batch.sent_at);

  // Render the batch title/body in the viewer's language when a bell
  // template exists for the kind (pushes are stored as-sent, which today
  // means a single hardcoded language). Templates that need metadata the
  // log doesn't carry (unfilled {{placeholders}}) and kinds without a
  // template fall back to the stored preview — same trick as the
  // NotificationBell rows.
  const localized = (sub: 'title' | 'body', fallback: string): string => {
    const out = t(`notifications.kind.${batch.kind}.${sub}`, { defaultValue: fallback });
    return !out || out.includes('{{') ? fallback : out;
  };
  const title = localized('title', batch.title_preview);
  const body = localized('body', batch.body_preview);

  return (
    <>
      <TableRow
        className="cursor-pointer"
        onClick={onToggle}
        data-state={expanded ? 'selected' : undefined}
      >
        <TableCell className="w-12 pr-0">
          <span className="flex h-8 w-8 items-center justify-center rounded-pill text-muted-foreground">
            <CaretRight className={cn('h-4 w-4 transition-transform duration-150', expanded && 'rotate-90')} />
          </span>
        </TableCell>

        <TableCell className="font-heading text-sm font-semibold">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate">{title || t('settings.log.push.preview.untitled')}</span>
            {batch.kind && batch.kind !== 'other' && (
              <span className="inline-flex shrink-0 items-center rounded-pill bg-muted px-2 py-0.5 font-heading text-[0.65rem] font-medium text-muted-foreground">
                {t(`settings.log.push.kind.${batch.kind}`, batch.kind)}
              </span>
            )}
          </div>
          {body && (
            <p className="mt-0.5 truncate text-xs font-normal text-muted-foreground">{body}</p>
          )}
          <p className="mt-0.5 text-xs font-normal text-muted-foreground sm:hidden">
            {dateFmt.format(sentAt)}
            {META_SEPARATOR}
            {t('settings.log.push.batch.recipients', { count: batch.total })}
          </p>
        </TableCell>

        <TableCell className="hidden w-40 whitespace-nowrap text-sm text-muted-foreground sm:table-cell">
          {dateFmt.format(sentAt)}
        </TableCell>

        <TableCell className="hidden w-24 text-right text-sm tabular-nums text-muted-foreground sm:table-cell">
          {batch.total}
        </TableCell>

        <TableCell className="hidden w-40 sm:table-cell">
          <div className="flex flex-wrap items-center gap-2 text-xs tabular-nums">
            {batch.sent_count > 0 && (
              <span className="text-foreground/70">{t('settings.log.push.batch.sent', { count: batch.sent_count })}</span>
            )}
            {batch.failed_count > 0 && (
              <span className="text-destructive">{t('settings.log.push.batch.failed', { count: batch.failed_count })}</span>
            )}
            {batch.skipped_count > 0 && (
              <span className="text-muted-foreground">{t('settings.log.push.batch.skipped', { count: batch.skipped_count })}</span>
            )}
          </div>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={5} className="p-0">
            {batch.total > 10 && (
              <div className="border-b border-border px-6 py-2.5">
                <div className="relative max-w-xs">
                  <MagnifyingGlass
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    value={recipientQ}
                    onChange={(e) => setRecipientQ(e.target.value)}
                    placeholder={t('settings.log.push.recipients.searchPlaceholder')}
                    aria-label={t('settings.log.push.recipients.searchPlaceholder')}
                    className="h-9 pl-9 text-xs"
                  />
                </div>
              </div>
            )}
            {recipientsQuery.isPending && (
              <p className="px-6 py-3 text-xs text-muted-foreground">{t('common.loading')}</p>
            )}
            {recipientsQuery.isError && (
              <p className="px-6 py-3 text-xs text-foreground">{t('settings.log.push.loadError')}</p>
            )}
            {recipientsQuery.data && recipientsQuery.data.items.length === 0 && (
              <p className="px-6 py-3 text-xs text-muted-foreground">
                {debouncedQ
                  ? t('settings.log.push.recipients.noMatches', { q: debouncedQ })
                  : t('settings.log.push.recipients.empty')}
              </p>
            )}
            {recipientsQuery.data && recipientsQuery.data.items.length > 0 && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-6 py-2 text-left font-heading font-semibold text-muted-foreground">
                      {t('settings.log.push.recipients.user')}
                    </th>
                    <th className="px-3 py-2 text-left font-heading font-semibold text-muted-foreground">
                      {t('settings.log.push.recipients.status')}
                    </th>
                    <th className="hidden px-3 py-2 text-left font-heading font-semibold text-muted-foreground sm:table-cell">
                      {t('settings.log.push.recipients.reason')}
                    </th>
                    <th className="w-24 px-3 py-2" aria-hidden />
                  </tr>
                </thead>
                <tbody>
                  {recipientsQuery.data.items.map((r) => (
                    <Fragment key={r.id}>
                      <tr className={cn('border-b border-border/40', !r.detail && 'last:border-0')}>
                        <td className="px-6 py-2">
                          <div className="font-heading font-semibold">{r.display_name || r.email || r.user_id}</div>
                          {r.platform && (
                            <div className="text-muted-foreground">{r.platform}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-pill px-2 py-0.5 font-heading text-[0.65rem] font-semibold',
                              pushStatusBadgeClass(r.status),
                            )}
                          >
                            {t(`settings.log.push.status.${r.status}`)}
                          </span>
                        </td>
                        <td className="hidden px-3 py-2 sm:table-cell">
                          <span className="inline-flex items-center rounded-pill bg-muted px-2 py-0.5 font-heading text-[0.65rem] font-medium text-muted-foreground">
                            {t(`settings.log.push.reason.${r.reason}`, { defaultValue: r.reason })}
                          </span>
                        </td>
                        <td className="w-24 px-3 py-2 text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPreviewID(r.id)}
                          >
                            <Eye className="h-3.5 w-3.5" weight="bold" aria-hidden />
                            {t('settings.log.push.recipients.view')}
                          </Button>
                        </td>
                      </tr>
                      {r.detail && (
                        <tr className="border-b border-border/40 last:border-0">
                          <td colSpan={4} className="px-6 pb-3 pt-0">
                            <div className="rounded-md bg-destructive/5 px-3 py-2">
                              <span className="font-heading text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('settings.log.push.recipients.errorDetail')}
                              </span>
                              <p className="mt-0.5 whitespace-pre-wrap break-words font-mono text-[0.7rem] text-foreground/80">
                                {r.detail}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </TableCell>
        </TableRow>
      )}

      <PushPreviewDialog
        open={previewID !== null}
        onOpenChange={(open) => { if (!open) setPreviewID(null); }}
        slug={slug}
        token={token}
        messageId={previewID}
      />
    </>
  );
}

